import STYLE_MANAGER from "../style-manager";

/**
 * Shadow-host custom element that also owns a modal host.
 *
 * The widget renders inside this element's own shadow root, while dialogs get a
 * separate shadow host appended to `document.body` — a dialog rendered inside
 * the widget's root is clipped by whatever theme markup the widget was injected
 * into (a cart drawer, a product form).
 *
 * Subclasses pass their modal host's id to `super()` and declare a
 * `static readonly TAG`; everything else is identical between them.
 */
export abstract class RedoModalHostElement extends HTMLElement {
  #deregisterStyles?: () => void;
  #deregisterModalStyles?: () => void;

  #root!: ShadowRoot;
  #modalRoot?: HTMLElement;
  #modalContainer!: HTMLElement;

  readonly #modalRootId: string;

  constructor(modalRootId: string) {
    super();
    this.#modalRootId = modalRootId;
  }

  get root() {
    return this.#root;
  }

  get modalContainer() {
    return this.#modalContainer;
  }

  connectedCallback() {
    if (!this.#root) {
      this.#root = this.attachShadow({ mode: "open" });
    }
    this.#deregisterStyles = STYLE_MANAGER.register(this.#root);

    const modalRoot = this.ownerDocument.createElement("div");
    modalRoot.setAttribute("id", this.#modalRootId);
    modalRoot.style.display = "initial";
    modalRoot.style.position = "absolute";
    modalRoot.style.zIndex = "50000";
    document.body.append(modalRoot);
    this.#modalRoot = modalRoot;
    const modalShadowRoot = modalRoot.attachShadow({ mode: "open" });
    this.#deregisterModalStyles = STYLE_MANAGER.register(modalShadowRoot);
    this.#modalContainer = this.ownerDocument.createElement("div");
    modalShadowRoot.append(this.#modalContainer);
  }

  disconnectedCallback() {
    this.#deregisterStyles?.();
    this.#deregisterModalStyles?.();
    this.#modalRoot?.remove();
    this.#modalRoot = undefined;
  }

  static register(
    this: CustomElementConstructor & { readonly TAG: string },
  ): void {
    try {
      if (!customElements.get(this.TAG)) {
        customElements.define(this.TAG, this);
      }
    } catch (error) {
      // Element might already be registered, which is fine.
    }
  }
}
